import React, { createContext, useContext, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../store';
import { login, register, logout, selectUser, selectToken, User, defaultInfluencerProfile, defaultAdvertiserProfile, verifyToken } from '../store/slices/authSlice';
import { useNavigate } from 'react-router-dom';
import { AppDispatch } from '../store';
import { api } from '../services/api';
import { toast } from 'react-hot-toast';
import { setProfile } from '../store/slices/profileSlice';
import { socket } from '../services/socket';

interface AuthContextType {
  user: User | null;
  token: string | null;
  loading: boolean;
  error: string | null;
  isInitializing: boolean;
  login: (email: string, password: string) => Promise<{ user: User; token: string }>;
  register: (userData: { email: string; password: string; username: string; role: 'influencer' | 'advertiser' }) => Promise<{ user: User; token: string }>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  const token = useSelector(selectToken);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);

  // Initialize auth state
  useEffect(() => {
    let isMounted = true;
    const initializeAuth = async () => {
      try {
        console.log('Initializing auth...');
        const token = localStorage.getItem('token');
        
        if (!token) {
          console.log('No token found, skipping initialization');
          if (isMounted) {
            setIsInitializing(false);
          }
          return;
        }

        // Set token in API headers
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        
        // Verify token and get user data
        const result = await dispatch(verifyToken()).unwrap();
        console.log('Token verification result:', {
          userId: result.user._id,
          role: result.user.role,
          hasToken: !!result.token
        });
        
        // Connect socket after successful token verification
        if (!socket.connected && isMounted) {
          console.log('Connecting socket after token verification...');
          socket.auth = { token };
          socket.connect();
        }
      } catch (error) {
        console.error('Auth initialization failed:', error);
        // Clear invalid token
        localStorage.removeItem('token');
        delete api.defaults.headers.common['Authorization'];
        socket.disconnect();
      } finally {
        if (isMounted) {
          setIsInitializing(false);
        }
      }
    };

    initializeAuth();

    return () => {
      isMounted = false;
    };
  }, [dispatch]);

  const handleLogin = async (email: string, password: string) => {
    try {
      setLoading(true);
      setError(null);
      const result = await dispatch(login({ email, password })).unwrap();
      navigate('/');
      return result;
    } catch (err: any) {
      setError(err.message || 'Login failed');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (userData: { email: string; password: string; username: string; role: 'influencer' | 'advertiser' }) => {
    try {
      setLoading(true);
      setError(null);
      const result = await dispatch(register(userData)).unwrap();
      navigate('/');
      return result;
    } catch (err: any) {
      setError(err.message || 'Registration failed');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      setLoading(true);
      await dispatch(logout()).unwrap();
      navigate('/');
    } catch (err: any) {
      setError(err.message || 'Logout failed');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const value = {
    user,
    token,
    loading,
    error,
    isInitializing,
    login: handleLogin,
    register: handleRegister,
    logout: handleLogout,
  };

  if (isInitializing) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '100vh',
        gap: '16px'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '4px solid #f3f3f3',
          borderTop: '4px solid #007AFF',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <div>Loading...</div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 